import type { ReactNode } from "react";
import { BOSH_USLUB } from "./scale";

export interface StatusMatrixColumn {
	key: string;
	label: string;
	/** Dam kuni kabi — sarlavha xira. */
	muted?: boolean;
}

export interface StatusMatrixCell {
	column: string;
	color: string;
	tooltip: string;
}

export interface StatusMatrixRow {
	key: string;
	label: ReactNode;
	cells: StatusMatrixCell[];
	/** Qator oxiridagi yakuniy qiymat (masalan davomat foizi). */
	trailing?: ReactNode;
}

export interface StatusMatrixProps {
	columns: StatusMatrixColumn[];
	rows: StatusMatrixRow[];
	/** Qator yo'q bo'lganda matn (reja imzosidan kengaytirilgan). */
	empty?: string;
	/** Jadval nomi — ekran o'quvchisi uchun (reja imzosidan kengaytirilgan). */
	ariaLabel?: string;
}

/**
 * Holat matritsasi (xodim × kun) — semantik `<table>`.
 *
 * ⚠️ **`role="img"` EMAS, jadval** — 14 × 31 katakni bitta
 * `aria-label` ga siqib bo'lmaydi; jadval navigatsiyasi esa har katakni
 * qator/ustun sarlavhasi bilan o'qiydi. Katak `aria-label` i holatni
 * SO'Z bilan aytadi (rang yolg'iz kanal emas).
 *
 * ⚠️ **antd `Tooltip` EMAS, `title`** — ~430 katakda har biriga
 * `Tooltip` o'rash `CompactDayStrip.tsx` da o'lchangan og'irlikni
 * takrorlardi.
 *
 * ⚠️ Yo'q katak — `var(--chart-grid)` va `title`siz (`data-empty`).
 *
 * ⚠️ `data-matrix-*` — TEST KONTRAKTI.
 */
export function StatusMatrix({ columns, rows, empty, ariaLabel }: StatusMatrixProps) {
	if (rows.length === 0 || columns.length === 0) {
		return (
			<div className="py-4 text-center text-xs" style={BOSH_USLUB}>
				{empty}
			</div>
		);
	}

	const trailingBor = rows.some((q) => q.trailing !== undefined && q.trailing !== null);

	return (
		<div className="overflow-x-auto">
			<table aria-label={ariaLabel} className="border-separate border-spacing-0.5 text-[10px]">
				<thead>
					<tr>
						<th />
						{columns.map((ustun) => (
							<th
								key={ustun.key}
								scope="col"
								data-matrix-column
								data-muted={ustun.muted ? "" : undefined}
								className="min-w-3.5 text-center font-normal tabular-nums"
								style={{ color: ustun.muted ? "var(--chart-neytral)" : "var(--chart-axis)" }}
							>
								{ustun.label}
							</th>
						))}
						{trailingBor ? <th /> : null}
					</tr>
				</thead>
				<tbody>
					{rows.map((qator) => (
						<MatritsaQatori
							key={qator.key}
							qator={qator}
							columns={columns}
							trailingBor={trailingBor}
						/>
					))}
				</tbody>
			</table>
		</div>
	);
}

function MatritsaQatori({
	qator,
	columns,
	trailingBor,
}: {
	qator: StatusMatrixRow;
	columns: StatusMatrixColumn[];
	trailingBor: boolean;
}) {
	const xarita = new Map(qator.cells.map((k) => [k.column, k]));

	return (
		<tr data-matrix-row>
			<th
				scope="row"
				className="pr-2 text-left text-xs font-normal"
				style={{ color: "var(--chart-ink)" }}
			>
				{/* `table-layout: auto` da `max-width` <th> ga ishonchli qo'llanmaydi — blokda qisqartiramiz */}
				<div
					className="max-w-40 truncate"
					title={typeof qator.label === "string" ? qator.label : undefined}
				>
					{qator.label}
				</div>
			</th>
			{columns.map((ustun) => {
				const katak = xarita.get(ustun.key);

				return (
					<td key={ustun.key} className="p-0">
						{katak ? (
							<div
								data-matrix-cell
								data-column={ustun.key}
								role="img"
								title={katak.tooltip}
								aria-label={katak.tooltip}
								className="h-3.5 w-3.5 rounded-sm"
								style={{ backgroundColor: katak.color }}
							/>
						) : (
							<div
								data-matrix-cell
								data-column={ustun.key}
								data-empty
								aria-hidden="true"
								className="h-3.5 w-3.5 rounded-sm"
								style={{ backgroundColor: "var(--chart-grid)" }}
							/>
						)}
					</td>
				);
			})}
			{trailingBor ? (
				<td
					data-matrix-trailing
					className="pl-2 text-right text-xs tabular-nums"
					style={{ color: "var(--chart-ink)" }}
				>
					{qator.trailing}
				</td>
			) : null}
		</tr>
	);
}
